import React, { useEffect, useMemo, useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../components/layout/MainLayout';
import AICallsTable from '../components/AICallsTable';
import AICallsSearchFilters from '../components/AICallsSearchFilters';
import { useAuth } from '../../../contexts/AuthContext';
import { useProfile } from '../../../hooks';
import { getUserCalls } from '../../../lib/api';
import { filterAICalls } from '../../../lib/utils/filterAICalls';
import {
  buildAICallsSearchParams,
  parseAICallsSearchParams,
} from '../../../lib/utils/aiCallsRoutes';
import { getCallStatusLabelKey } from '../../../lib/utils/callStatus';
import { planHasVoiceAI } from '../../../lib/utils/planEntitlements';

const formatFilterDate = (value) => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'dd-MM-yyyy');
  } catch {
    return value;
  }
};

const AIAssistantCalls = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { profile, loading: profileLoading } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseAICallsSearchParams(searchParams),
    [searchParams],
  );
  const syncedUrlParamsRef = useRef(searchParams.toString());
  const hasLoadedRef = useRef(false);

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(urlState.candidateSearch);
  const [appliedCandidateSearch, setAppliedCandidateSearch] = useState(urlState.candidateSearch);
  const [dateFilter, setDateFilter] = useState(urlState.dateFilter);
  const [statusFilter, setStatusFilter] = useState(urlState.statusFilter);

  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const hasVoiceAI = planHasVoiceAI(plan);
  const hasActiveFilters = Boolean(
    appliedCandidateSearch.trim() || dateFilter || statusFilter !== 'all',
  );

  useEffect(() => {
    const currentParams = searchParams.toString();
    if (currentParams === syncedUrlParamsRef.current) {
      return;
    }

    syncedUrlParamsRef.current = currentParams;
    setSearchInput(urlState.candidateSearch);
    setAppliedCandidateSearch(urlState.candidateSearch);
    setDateFilter(urlState.dateFilter);
    setStatusFilter(urlState.statusFilter);
  }, [searchParams, urlState]);

  useEffect(() => {
    const nextParams = buildAICallsSearchParams({
      candidateSearch: appliedCandidateSearch,
      dateFilter,
      statusFilter,
    });
    const nextParamsString = nextParams.toString();

    if (nextParamsString === syncedUrlParamsRef.current) {
      return;
    }

    syncedUrlParamsRef.current = nextParamsString;
    setSearchParams(nextParams, { replace: true });
  }, [appliedCandidateSearch, dateFilter, statusFilter, setSearchParams]);

  const handleApplySearch = () => {
    setAppliedCandidateSearch(searchInput.trim());
  };

  const filteredCalls = useMemo(
    () => filterAICalls(calls, {
      candidateSearch: appliedCandidateSearch,
      dateFilter,
      statusFilter,
    }),
    [calls, appliedCandidateSearch, dateFilter, statusFilter],
  );

  const totalCount = filteredCalls.length;

  const clearAllFilters = () => {
    setSearchInput('');
    setAppliedCandidateSearch('');
    setDateFilter('');
    setStatusFilter('all');
  };

  const statusFilterLabel = statusFilter !== 'all' && getCallStatusLabelKey(statusFilter)
    ? t(getCallStatusLabelKey(statusFilter))
    : statusFilter;

  useEffect(() => {
    const fetchCalls = async () => {
      if (!userId || (profileLoading && !profile)) return;

      if (!hasVoiceAI) {
        setLoading(false);
        hasLoadedRef.current = false;
        return;
      }

      const useSilent = hasLoadedRef.current;

      try {
        if (!useSilent) setLoading(true);
        const userCalls = await getUserCalls(userId);
        setCalls(userCalls || []);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('Error fetching AI calls:', error);
      } finally {
        if (!useSilent) setLoading(false);
      }
    };

    fetchCalls();
  }, [userId, hasVoiceAI, profileLoading, profile]);

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
            {t('ai.callsTitle')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('ai.callsSubtitle')}
          </p>
        </div>

        {loading || (profileLoading && !profile) ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : hasVoiceAI ? (
          <>
            <AICallsSearchFilters
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              onSearchSubmit={handleApplySearch}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              totalCalls={totalCount}
            />

            {hasActiveFilters && !loading && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('ai.calls.activeFilters')}</span>
                {appliedCandidateSearch.trim() && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {t('ai.calls.filterSearch', { term: appliedCandidateSearch })}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        setAppliedCandidateSearch('');
                      }}
                      className="hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {dateFilter && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">
                    {t('ai.calls.filterDateChip', { date: formatFilterDate(dateFilter) })}
                    <button
                      type="button"
                      onClick={() => setDateFilter('')}
                      className="hover:bg-purple-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">
                    {t('ai.calls.filterStatusChip', { label: statusFilterLabel })}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('all')}
                      className="hover:bg-teal-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  {t('common.clearAll')}
                </button>
              </div>
            )}

            {!loading && totalCount > 0 && (
              <div className="mb-4 text-sm text-muted-foreground">
                {t('ai.calls.showing')}{' '}
                <span className="font-semibold text-foreground">{totalCount}</span>{' '}
                {totalCount !== 1 ? t('ai.calls.calls') : t('ai.calls.call')}
                {hasActiveFilters && t('ai.calls.matchingFilters')}
              </div>
            )}

            {!loading && totalCount === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <div className="text-muted-foreground mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{t('ai.calls.emptyTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {hasActiveFilters
                    ? t('ai.calls.emptyFiltered')
                    : t('ai.calls.emptyDefault')}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-sm text-primary hover:text-accent underline"
                  >
                    {t('ai.calls.clearAllFilters')}
                  </button>
                )}
              </div>
            ) : (
              !loading && <AICallsTable calls={filteredCalls} />
            )}
          </>
        ) : null}
      </div>
    </MainLayout>
  );
};

export default AIAssistantCalls;
